import { parse as parsePokesolText } from '@pokesol/pokesol-text-parser-ts';
import { getItemNameById, getMoveNameById } from '../utils';
import { formatPokesolDisplayName } from '../utils/pokesol-text';
import { PokemonV2 } from '../pokemon/pokemon';
import { calculateActualStats } from './stat';
import { StatInputs } from '../types/stat';
import { type_id_to_hiraganakatakana } from './type';
import { Personality } from '../types/damage';
import { MAX_STAT_EFFORT } from '../constants/effort';

const LEGACY_POKESOL_MAX_STAT_EFFORT = 252;

// ポケソル共有用に32基準の努力値を旧252基準へ変換する
const convertEffortForPokesol = (effort: number): number => {
  if (!Number.isFinite(effort)) return 0;
  const normalizedEffort = Math.max(0, Math.min(MAX_STAT_EFFORT, Math.round(effort)));
  if (normalizedEffort === 0) {
    return 0;
  }
  // 旧仕様の努力値は 4(1),12(2),...,252(32) の並びに合わせる
  const legacyEffort = normalizedEffort * 8 - 4;
  return Math.min(LEGACY_POKESOL_MAX_STAT_EFFORT, legacyEffort);
};

const formatPokesolEffort = (effort: number): string => {
  const legacyEffort = convertEffortForPokesol(effort);
  return legacyEffort > 0 ? `(${legacyEffort})` : '';
};

// 生成したポケソルテキストが公式パーサーで読めるか簡易チェック
const ensureParsableByPokesol = (text: string): void => {
  try {
    parsePokesolText(text);
  } catch (error) {
    // パースできない場合でもUIを止めない。ログだけ残す。
    console.error('ポケソルテキストのパース検証に失敗しました', error);
  }
};

// ライブラリ側の正規表現制約を簡易的に満たしているか判定
const isPokesolParserFriendly = (params: {
  itemName?: string;
  abilityName?: string;
  natureName?: string;
  moveNames: string[];
}): boolean => {
  // 漢字を含む名称だとOSSパーサーが落ちるため、事前に許容文字か判定する
  const itemOk = params.itemName ? /^[ぁ-んァ-ヶー]+$/.test(params.itemName) : true;
  const abilityOk = params.abilityName ? /^[ぁ-んァ-ヶー]+$/.test(params.abilityName) : true;
  const natureOk = params.natureName ? /^[ぁ-ん]+$/.test(params.natureName) : true;
  const movesOk = params.moveNames.every((name) => /^[０-９Ａ-Ｚぁ-んァ-ヶー・]+$/.test(name));
  return itemOk && abilityOk && natureOk && movesOk;
};

function getPersonalityName(personality: Personality): string {
  const stats: Array<keyof Personality> = ['attack', 'defense', 'special_attack', 'special_defense', 'speed'];
  const upCount = stats.filter((stat) => personality[stat] === 1.1).length;
  const downCount = stats.filter((stat) => personality[stat] === 0.9).length;
  const personalityMap: Record<string, string> = {
    '1,1,1,1,1': 'がんばりや',
    '1.1,0.9,1,1,1': 'さみしがり',
    '1.1,1,0.9,1,1': 'いじっぱり',
    '1.1,1,1,0.9,1': 'やんちゃ',
    '1.1,1,1,1,0.9': 'ゆうかん',
    '0.9,1.1,1,1,1': 'ずぶとい',
    '1,1.1,0.9,1,1': 'わんぱく',
    '1,1.1,1,0.9,1': 'のうてんき',
    '1,1.1,1,1,0.9': 'のんき',
    '0.9,1,1.1,1,1': 'ひかえめ',
    '1,0.9,1.1,1,1': 'おっとり',
    '1,1,1.1,0.9,1': 'うっかりや',
    '1,1,1.1,1,0.9': 'れいせい',
    '0.9,1,1,1.1,1': 'おだやか',
    '1,0.9,1,1.1,1': 'おとなしい',
    '1,1,0.9,1.1,1': 'しんちょう',
    '1,1,1,1.1,0.9': 'なまいき',
    '0.9,1,1,1,1.1': 'おくびょう',
    '1,0.9,1,1,1.1': 'せっかち',
    '1,1,0.9,1,1.1': 'ようき',
    '1,1,1,0.9,1.1': 'むじゃき',
  };
  if (upCount === 1 && downCount === 1) {
    const key = [personality.attack, personality.defense, personality.special_attack, personality.special_defense, personality.speed].join(',');
    return personalityMap[key] || 'きまぐれ';
  }
  if (upCount === 0 && downCount === 0) return 'がんばりや';
  if (upCount === 1 && downCount === 0) {
    const upStat = stats.find((stat) => personality[stat] === 1.1);
    if (upStat === 'attack') {
      const newP = { ...personality, special_attack: 0.9 as const };
      const key = [newP.attack, newP.defense, newP.special_attack, newP.special_defense, newP.speed].join(',');
      return personalityMap[key] || 'きまぐれ';
    } else {
      const newP = { ...personality, attack: 0.9 as const };
      const key = [newP.attack, newP.defense, newP.special_attack, newP.special_defense, newP.speed].join(',');
      return personalityMap[key] || 'きまぐれ';
    }
  }
  if (upCount === 0 && downCount === 1) {
    const downStat = stats.find((stat) => personality[stat] === 0.9);
    if (downStat === 'attack') {
      const newP = { ...personality, special_attack: 1.1 as const };
      const key = [newP.attack, newP.defense, newP.special_attack, newP.special_defense, newP.speed].join(',');
      return personalityMap[key] || 'きまぐれ';
    } else {
      const newP = { ...personality, special_attack: 0.9 as const };
      const key = [newP.attack, newP.defense, newP.special_attack, newP.special_defense, newP.speed].join(',');
      return personalityMap[key] || 'きまぐれ';
    }
  }
  return 'きまぐれ';
}

export function createPokesolText(pokemon: PokemonV2, stat: StatInputs): string {
  const itemName = (): string | undefined => {
    if ('selected_item_id' in pokemon && pokemon.selected_item_id) {
      return getItemNameById(pokemon.selected_item_id);
    } else if ('selected_item_id' in pokemon && pokemon.selected_item_id) {
      return getItemNameById(pokemon.selected_item_id);
    }
    return undefined;
  };
  const itemNameStr = itemName();
  const itemNameFormat = itemNameStr ? ` @ ${itemNameStr}` : '';
  // テラスタイプ未設定時はステラ表示に揃える
  const terasuTypeName = pokemon.terasu_type ? type_id_to_hiraganakatakana(pokemon.terasu_type) : 'ステラ';

  const effective_value_hpFormat = formatPokesolEffort(stat.hp_effort);
  const effective_value_attackFormat = formatPokesolEffort(stat.attack_effort);
  const effective_value_defenceFormat = formatPokesolEffort(stat.defense_effort);
  const effective_value_special_attackFormat = formatPokesolEffort(stat.special_attack_effort);
  const effective_value_special_defenceFormat = formatPokesolEffort(stat.special_defense_effort);
  const effective_value_speedFormat = formatPokesolEffort(stat.speed_effort);

  const actualStats = calculateActualStats(pokemon, stat);
  const { hp: actual_hp, attack: actual_attack, defense: actual_defence, specialAttack: actual_special_attack, specialDefense: actual_special_defence, speed: actual_speed } = actualStats;

  const moveNameList: string[] = [];
  const ids = (pokemon as any).selected_move_ids as number[] | undefined;
  // 選択技IDから表示名を引く（未設定は空文字でスキップ）
  if (ids && ids[0]) moveNameList.push(getMoveNameById(ids[0]) || '');
  if (ids && ids[1]) moveNameList.push(getMoveNameById(ids[1]) || '');
  if (ids && ids[2]) moveNameList.push(getMoveNameById(ids[2]) || '');
  if (ids && ids[3]) moveNameList.push(getMoveNameById(ids[3]) || '');
  const monveNameListSeparatedString = moveNameList.filter(Boolean).join(' / ');

  const pokemonName = formatPokesolDisplayName(pokemon.name_ja);
  const abilityName = pokemon.selected_ability?.name_ja;
  const personalityName = getPersonalityName(pokemon.personality);
  const parserFriendly = isPokesolParserFriendly({
    itemName: itemNameStr,
    abilityName,
    natureName: personalityName,
    moveNames: moveNameList.filter(Boolean),
  });

  const text = `${pokemonName}${itemNameFormat}
テラスタイプ: ${terasuTypeName || ''}
特性: ${abilityName}
性格: ${personalityName || '未設定'}
${actual_hp}${effective_value_hpFormat}-${actual_attack}${effective_value_attackFormat}-${actual_defence}${effective_value_defenceFormat}-${actual_special_attack}${effective_value_special_attackFormat}-${actual_special_defence}${effective_value_special_defenceFormat}-${actual_speed}${effective_value_speedFormat}
${monveNameListSeparatedString}`;

  // OSSパーサーで正しく読めることを確認し、フォーマット逸脱を検知する
  if (parserFriendly) {
    ensureParsableByPokesol(text);
  }

  return text;
}
